import { useCallback, useEffect, useState } from "react";
import { Download, MagnifyingGlass, Plus, PencilSimple, Trash, AddressBook, UserCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, authHeaders, BASE_URL, getToken } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("es-US", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function Customers() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} (new) | customer object
    const [detail, setDetail] = useState(null);   // customer key being viewed

    const load = useCallback(async (query = "") => {
        setLoading(true);
        try {
            const r = await api.get("/admin/customers", {
                params: query ? { q: query } : {},
                headers: authHeaders(),
            });
            setRows(r.data.items || []);
            setTotal(r.data.total || 0);
        } catch {
            toast.error("No se pudieron cargar los clientes");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Debounced live search
    useEffect(() => {
        const t = setTimeout(() => load(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q, load]);

    const del = async (c) => {
        if (!window.confirm(`¿Eliminar a "${c.name || c.email || c.phone}"? Esta acción no se puede deshacer.`)) return;
        try {
            await api.delete(`/admin/customers/id/${c.id}`, { headers: authHeaders() });
            toast.success("Cliente eliminado");
            load(q.trim());
        } catch {
            toast.error("No se pudo eliminar");
        }
    };

    const csvUrl = `${BASE_URL}/api/admin/customers.csv?token=${encodeURIComponent(getToken() || "")}`;

    return (
        <div data-testid="admin-customers">
            <PageHeader
                kicker="CRM"
                title="Clientes"
                action={
                    <div className="flex items-center gap-2 flex-wrap">
                        <a
                            href={csvUrl}
                            data-testid="customers-export-csv"
                            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 font-bold uppercase tracking-widest text-xs hover:-translate-y-0.5 transition"
                        >
                            <Download size={15} weight="bold" />
                            CSV
                        </a>
                        <button
                            data-testid="customers-add-btn"
                            onClick={() => setEditing({})}
                            className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:-translate-y-0.5 transition"
                        >
                            <Plus size={15} weight="bold" />
                            Nuevo cliente
                        </button>
                    </div>
                }
            />

            <div className="relative mb-4 max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                    data-testid="customers-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar por nombre, teléfono o correo..."
                    className="pl-9 rounded-full bg-white"
                />
            </div>

            <div className="bg-white rounded-2xl border border-border overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-muted-foreground">Cargando...</div>
                ) : rows.length === 0 ? (
                    <div data-testid="customers-empty" className="p-12 text-center text-muted-foreground">
                        <AddressBook size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
                        {q ? "Sin resultados para esa búsqueda." : "Todavía no hay clientes registrados."}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="customers-table">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                                    <th className="px-4 py-3">Cliente</th>
                                    <th className="px-4 py-3">Teléfono</th>
                                    <th className="px-4 py-3">Correo</th>
                                    <th className="px-4 py-3">Dirección</th>
                                    <th className="px-4 py-3 text-center">Pedidos</th>
                                    <th className="px-4 py-3 text-right">Gastado</th>
                                    <th className="px-4 py-3">Último pedido</th>
                                    <th className="px-4 py-3 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c, i) => (
                                    <tr
                                        key={c.id || i}
                                        className="border-t border-border hover:bg-muted/20 cursor-pointer"
                                        data-testid={`customer-row-${i}`}
                                        onClick={() => setDetail(c.key)}
                                    >
                                        <td className="px-4 py-3">
                                            <span className="font-bold">{c.name || "—"}</span>
                                            {(c.has_account || c.password_hash) && (
                                                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                                                    <UserCircle size={10} weight="bold" /> Cuenta
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">{c.phone || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground max-w-[180px] truncate">{c.address || "—"}</td>
                                        <td className="px-4 py-3 text-center font-bold">{c.total_orders || 0}</td>
                                        <td className="px-4 py-3 text-right font-display font-black">{money(c.total_spent)}</td>
                                        <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{fmtDate(c.last_order_at)}</td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                data-testid={`customer-edit-${i}`}
                                                onClick={() => setEditing(c)}
                                                className="h-8 w-8 inline-grid place-items-center rounded-full hover:bg-muted transition"
                                                title="Editar"
                                            >
                                                <PencilSimple size={15} weight="bold" />
                                            </button>
                                            <button
                                                data-testid={`customer-delete-${i}`}
                                                onClick={() => del(c)}
                                                className="h-8 w-8 inline-grid place-items-center rounded-full text-muted-foreground hover:text-destructive hover:bg-red-50 transition"
                                                title="Eliminar"
                                            >
                                                <Trash size={15} weight="bold" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <p className="mt-4 text-xs text-muted-foreground" data-testid="customers-count">
                {total} {total === 1 ? "cliente" : "clientes"} en la base de datos.
            </p>

            {editing !== null && (
                <CustomerFormDialog
                    customer={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(q.trim()); }}
                />
            )}
            {detail && <CustomerDetailDialog customerKey={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function CustomerFormDialog({ customer, onClose, onSaved }) {
    const isNew = !customer.id;
    const [form, setForm] = useState({
        name: customer.name || "",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        notes: customer.notes || "",
    });
    const [busy, setBusy] = useState(false);
    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const save = async () => {
        if (!form.name.trim()) return toast.error("El nombre es requerido");
        if (isNew && !form.phone.trim() && !form.email.trim())
            return toast.error("Agrega al menos un teléfono o correo");
        setBusy(true);
        try {
            const payload = { ...form, email: form.email.trim() || null };
            if (isNew) {
                await api.post("/admin/customers", payload, { headers: authHeaders() });
                toast.success("Cliente creado");
            } else {
                await api.put(`/admin/customers/id/${customer.id}`, payload, { headers: authHeaders() });
                toast.success("Cliente actualizado");
            }
            onSaved();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "No se pudo guardar");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="customer-form-dialog" className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-black tracking-tight">
                        {isNew ? "Nuevo cliente" : "Editar cliente"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Formulario de datos del cliente</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <Input name="name" placeholder="Nombre completo *" value={form.name} onChange={onChange} data-testid="customer-form-name" />
                    <Input name="phone" placeholder="Teléfono" value={form.phone} onChange={onChange} data-testid="customer-form-phone" />
                    <Input name="email" type="email" placeholder="Correo electrónico" value={form.email} onChange={onChange} data-testid="customer-form-email" />
                    <Input name="address" placeholder="Dirección" value={form.address} onChange={onChange} data-testid="customer-form-address" />
                    <Input name="notes" placeholder="Notas internas (opcional)" value={form.notes} onChange={onChange} data-testid="customer-form-notes" />
                    <Button
                        data-testid="customer-form-save"
                        onClick={save}
                        disabled={busy}
                        className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest text-xs py-5"
                    >
                        {busy ? "..." : isNew ? "Crear cliente" : "Guardar cambios"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function CustomerDetailDialog({ customerKey, onClose }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/admin/customers/${encodeURIComponent(customerKey)}`, { headers: authHeaders() })
            .then((r) => setData(r.data))
            .catch(() => {
                toast.error("No se pudo cargar el detalle");
                onClose();
            });
    }, [customerKey, onClose]);

    const c = data?.customer;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="customer-detail-dialog" className="sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl">
                {!data ? (
                    <div className="p-8 text-center text-muted-foreground">Cargando...</div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl font-black tracking-tight">
                                {c.name || c.email || c.phone}
                            </DialogTitle>
                            <DialogDescription className="sr-only">Detalle e historial del cliente</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-3 text-sm mt-1">
                            <InfoCell label="Teléfono" value={c.phone} />
                            <InfoCell label="Correo" value={c.email} />
                            <InfoCell label="Dirección" value={c.address} span />
                            <InfoCell label="Pedidos" value={String(c.total_orders || 0)} />
                            <InfoCell label="Total gastado" value={money(c.total_spent)} />
                            <InfoCell label="Primer pedido" value={fmtDate(c.first_order_at)} />
                            <InfoCell label="Último pedido" value={fmtDate(c.last_order_at)} />
                            {c.notes && <InfoCell label="Notas" value={c.notes} span />}
                        </div>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-4 mb-2">
                            Historial de compras
                        </p>
                        {(data.orders || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">Sin pedidos pagados.</p>
                        ) : (
                            <div className="space-y-2" data-testid="customer-detail-orders">
                                {data.orders.map((o) => (
                                    <div key={o.id} className="bg-muted/50 rounded-xl px-3 py-2.5">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs text-muted-foreground">
                                                {o.created_at ? new Date(o.created_at).toLocaleString() : ""} · {o.channel || "web"} · {o.status}
                                            </p>
                                            <span className="font-display font-black text-sm">{money(o.total)}</span>
                                        </div>
                                        <p className="text-sm font-semibold mt-0.5">
                                            {(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function InfoCell({ label, value, span }) {
    return (
        <div className={span ? "col-span-2" : ""}>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
            <p className="font-semibold">{value || "—"}</p>
        </div>
    );
}
